import React from "react"
import { useSideNav } from "hooks/useSideNav"
import { Button } from "."
import { AnchorButtonParser } from "./AnchorButtonParser"
import { Variant } from "./buttons-types"
import { PhoneLinkParser } from "./PhoneLinkParser"
import { getRelativePathFromCodex } from "./utils/getRelativePathFromCodex"
import { useLayout } from "contexts/components/LayoutContext"

interface CTAProps {
  anchorReference: string
  componentType: "anchor_button" | "button"
  customPhoneNumber: string
  extraClassNames: string
  index: number
  label: string | null
  onGetCTAVariant: (index: number) => Variant
  showPhoneIcon?: boolean
  slug: string | null
  usesMainOfficePhoneLink: boolean
}

export const CTA = ({
  anchorReference,
  componentType,
  customPhoneNumber,
  extraClassNames,
  index,
  label,
  onGetCTAVariant,
  showPhoneIcon = false,
  slug,
  usesMainOfficePhoneLink,
}: CTAProps) => {
  const { linkedPagesUrlPathCodex } = useLayout()
  const { handleCloseNav } = useSideNav()

  const getCTAClassName = (index: number) => {
    const ctaClassName = `button--${onGetCTAVariant(index)} ${extraClassNames}`
    return ctaClassName
  }

  const hasPhoneNumber = !!customPhoneNumber

  if (usesMainOfficePhoneLink || hasPhoneNumber) {
    return (
      <PhoneLinkParser
        className={getCTAClassName(index)}
        customPhoneNumber={customPhoneNumber}
        showIcon={showPhoneIcon}
        useMainOfficeTrackingNumber={usesMainOfficePhoneLink}
      >
        {label ?? ""}
      </PhoneLinkParser>
    )
  }

  const formattedSlug = getRelativePathFromCodex({
    codex: linkedPagesUrlPathCodex,
    path: slug,
    codexKeyStartsWithForwardSlash: false,
  })

  if (componentType === "anchor_button") {
    return (
      <AnchorButtonParser
        className={getCTAClassName(index)}
        extraClassNames={extraClassNames}
        reference={anchorReference}
        showIcon
        slug={formattedSlug}
        variant={onGetCTAVariant(index)}
      >
        {label}
      </AnchorButtonParser>
    )
  }

  return (
    <Button
      extraClassNames={extraClassNames}
      onClick={handleCloseNav}
      to={formattedSlug}
      variant={onGetCTAVariant(index)}
    >
      {label}
    </Button>
  )
}
